import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useShowAiStudio } from "@design-lab/shared/lib/config";
import { trackAiStudioCtaClicked } from "@design-lab/shared/lib/analytics";
import { useDesignProjectSync } from "@design-lab/features/design-project/model/useDesignProjectSync";
import { useArtwork } from "@design-lab/entities/artwork";
import type { Side } from "@design-lab/shared";

interface AiStudioButtonProps {
  side: Side;
}

export function AiStudioButton({ side }: AiStudioButtonProps) {
  const { isDesktop } = useResponsive();
  const [searchParams] = useSearchParams();
  const shouldShowAiStudio = useShowAiStudio();
  const { someArtworkLoading } = useArtwork();
  const { isRestoring } = useDesignProjectSync();

  const isAiStudioDisabled = useMemo(
    () => someArtworkLoading || isRestoring,
    [someArtworkLoading, isRestoring]
  );

  const aiStudioButtonClassName = useMemo(() => {
    const baseClasses = `ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center whitespace-nowrap rounded-md bg-indigo-600 font-medium text-white transition-colors hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
      isDesktop ? "h-12 px-8 text-sm" : "h-8 px-2 text-xs"
    } px-2 lg:px-4`;
    return isAiStudioDisabled ? `${baseClasses} opacity-50 cursor-not-allowed` : baseClasses;
  }, [isAiStudioDisabled, isDesktop]);

  const aiStudioButtonTitle = useMemo(() => {
    if (isRestoring) {
      return "Please wait, we are preparing your design";
    }
    return isAiStudioDisabled
      ? "Please wait, we are processing your design"
      : "Create AI-generated designs";
  }, [isAiStudioDisabled, isRestoring]);

  const aiStudioButtonAriaLabel = useMemo(() => {
    if (isRestoring) {
      return "Design with AI (disabled - please wait, we are preparing your design)";
    }
    return isAiStudioDisabled
      ? "Design with AI (disabled - please wait, we are processing your design)"
      : "Design with AI - Create AI-generated designs";
  }, [isAiStudioDisabled, isRestoring]);

  const handleAiStudioClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isAiStudioDisabled) {
        e.preventDefault();
        return;
      }
      trackAiStudioCtaClicked(side);
    },
    [isAiStudioDisabled, side]
  );

  const aiStudioHref = useMemo(() => {
    const baseUrl = `/weaver-ai-studio${searchParams.get("catalog_number") ? `/${searchParams.get("catalog_number")}` : ""}`;
    const url = new URL(baseUrl, window.location.origin);

    // Add current query parameters
    const currentParams = new URLSearchParams(window.location.search);

    // Add side parameter
    url.searchParams.set("side", side);

    // Preserve other relevant query parameters
    ["ac", "made", "page", "v", "sample"].forEach(param => {
      if (currentParams.has(param)) {
        url.searchParams.set(param, currentParams.get(param)!);
      }
    });

    return url.pathname + url.search;
  }, [searchParams, side]);

  if (!shouldShowAiStudio) {
    return null;
  }

  return (
    <a
      data-testid="ai-studio-button"
      href={aiStudioHref}
      className={aiStudioButtonClassName}
      title={aiStudioButtonTitle}
      aria-label={aiStudioButtonAriaLabel}
      onClick={handleAiStudioClick}
    >
      Design with AI
    </a>
  );
}
